import React from "react";
import { motion } from "framer-motion";

// A styled run of text within a heading, e.g. the italic accent phrase in
// "Driving Philippine <Cold Chain> Excellence". `breakAfter` inserts a <br/>
// once this segment's words are done (for headings with explicit line breaks).
export type TextSegment = { text: string; className?: string; breakAfter?: boolean };

// Renders a heading/paragraph as individually staggered word spans that fade
// and slide up as the text scrolls into view, per the "text reveal" technique
// in https://prismic.io/blog/css-scroll-effects. Accepts either a plain
// `text` string or `segments` for headings with mixed inline styling.
const AnimatedText: React.FC<{
  as: React.ElementType;
  text?: string;
  segments?: TextSegment[];
  className?: string;
  wordStagger?: number;
  amount?: number;
  /** 'inView' (default) replays on every scroll into view; 'mount' plays once
   *  whenever this component (re-)mounts, e.g. inside an AnimatePresence key
   *  change, for content that isn't scroll-driven. */
  mode?: "inView" | "mount";
  delayChildren?: number;
}> = ({ as: Tag, text, segments, className, wordStagger = 0.06, amount = 0.5, mode = "inView", delayChildren = 0 }) => {
  const finalSegments: TextSegment[] = segments ?? (text ? [{ text }] : []);

  const tokens: { word: string; className?: string; space: boolean; breakAfter?: boolean }[] = [];
  finalSegments.forEach((seg, si) => {
    const words = seg.text.trim().split(/\s+/).filter(Boolean);
    words.forEach((word, wi) => {
      const isLastWordOfSeg = wi === words.length - 1;
      tokens.push({
        word,
        className: seg.className,
        space: !(isLastWordOfSeg && (seg.breakAfter || si === finalSegments.length - 1)),
        breakAfter: isLastWordOfSeg && seg.breakAfter,
      });
    });
  });

  const orchestration = { transition: { staggerChildren: wordStagger, delayChildren } };
  const triggerProps =
    mode === "mount"
      ? { initial: "hidden", animate: "visible" }
      : { initial: "hidden", whileInView: "visible", viewport: { once: false, amount } };

  return (
    <Tag
      className={className}
      {...triggerProps}
      variants={{ hidden: {}, visible: orchestration }}
    >
      {tokens.map((t, i) => (
        <React.Fragment key={i}>
          <motion.span
            className={t.className}
            style={{ display: "inline-block" }}
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
          >
            {t.word}
          </motion.span>
          {t.space ? " " : ""}
          {t.breakAfter ? <br /> : null}
        </React.Fragment>
      ))}
    </Tag>
  );
};

export default AnimatedText;
